import {
  Component,
  Host,
  h,
  Prop,
  State,
  Element,
  Event,
  EventEmitter,
} from '@stencil/core';
import { apis } from './../../globals/resources';
import { loadFontAwesome } from '../../utils/utils';
import {
  ContactFormField,
  ContactFormFieldOption,
} from '../../services/internal-api/types';

@Component({
  tag: 'webcomet-contact-form',
  styleUrl: 'contact-form.scss',
  shadow: true,
})
export class ContactForm {
  @Element() el: HTMLWebcometContactFormElement;

  @State() loading = false;

  @State() submitted = false;

  @State() errors = {};

  /**
   * Custom JavaScript to execute when component has been loaded
   */
  @Prop() customJs: string;

  /**
   * The endpoint url to receieve the form data
   */
  @Prop() endpoint: string;

  /**
   * The fields to render in the form
   */
  @Prop() fields: ContactFormField[] = null;

  /**
   * Heading text to show above the form
   */
  @Prop() heading: string = null;

  /**
   * Description text to show above the form
   */
  @Prop() description: string = null;

  /**
   * The current language used
   */
  @Prop() language: string = null;

  /**
   * The text to display on the submit button
   */
  @Prop() submitButtonText: string = null;

  /**
   * Message to show when the form has been submitted successfully
   */
  @Prop() successMessage: string = null;

  /**
   * Base url to the webcomet installation to fetch data from
   * @internal
   */
  @Prop() webcometUrl: string = apis.webcometUrl;

  /**
   * Event emitted when the form is changed
   */
  @Event() formChange: EventEmitter<{ [key: string]: string }>;

  /**
   * Event emitted when form validation fails.
   * Containing the scroll position of the form-group
   * with the first input with validation errors
   */
  @Event() scrollTo: EventEmitter<number>;

  private serializeForm(form: HTMLFormElement) {
    const formData = new FormData(form);
    const serialized = {};
    formData.forEach((value, key) => {
      serialized[key] = value;
    });

    return serialized;
  }

  private async handleSubmit(event: Event) {
    event.preventDefault();

    this.loading = true;

    this.errors = {};

    const payload = this.serializeForm(event.target as HTMLFormElement);

    const url = `${this.endpoint}?language=${this.language}`;

    const response = await window.fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        /* eslint-disable @typescript-eslint/naming-convention */
        'Content-Type': 'application/json',
        /* eslint-enable @typescript-eslint/naming-convention */
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const UNPROCESSABLE_ENTITY = 422;
      if (response.status === UNPROCESSABLE_ENTITY) {
        const data = await response.json();
        this.errors = {
          ...data.errors,
        };
        window.requestAnimationFrame(() => this.focusFirstError());
      } else {
        alert(`Request failed: ${response.status}`);
      }
    } else {
      this.submitted = true;
    }

    this.loading = false;
  }

  private focusFirstError() {
    const errorInput = this.el.shadowRoot.querySelector(
      '.form__control--error'
    ) as HTMLElement;
    const formGroup = errorInput.parentNode as HTMLElement;
    errorInput.focus();
    const formEl = this.el.shadowRoot.querySelector('form');
    this.scrollTo.emit(formGroup.offsetTop - formEl.offsetTop);
  }

  private renderHeader() {
    return this.heading || this.description ? (
      <div class="header">
        {this.heading ? <h1 class="header__heading">{this.heading}</h1> : ''}
        {this.description ? (
          <p
            class="header__description"
            style={{
              marginTop: this.heading ? '0.625em' : '0',
            }}
          >
            {this.description}
          </p>
        ) : (
          ''
        )}
      </div>
    ) : (
      ''
    );
  }

  private renderSuccessMessage() {
    return (
      <div class="success">
        <div>
          <i class="fa fa-check-circle success__icon"></i>
          <p class="success__message">{this.successMessage}</p>
        </div>
      </div>
    );
  }

  private renderSubmitButton() {
    if (this.fields.length === 0) {
      return;
    }

    return (
      <button type="submit" class="form__submit" disabled={this.loading}>
        {this.loading ? (
          <i class="fa fa-spinner fa-spin"></i>
        ) : (
          this.submitButtonText
        )}
      </button>
    );
  }

  private renderField(field: ContactFormField) {
    const fieldName = this.getFieldName(field);

    const attrs = {
      id: this.getFieldId(field),
      className: 'form__control',
      placeholder: field.placeholder,
      name: fieldName,
      disabled: this.loading,
      ariaRequired: field.required,
      ariaInvalid: false,
      ariaDescribedBy: null,
    };

    if (this.errors[fieldName]) {
      attrs.className += ' form__control--error';
      attrs.ariaInvalid = true;
      attrs.ariaDescribedBy = `${fieldName}-error`;
    }

    switch (field.type) {
      case 'text':
        return <input type="text" {...attrs}></input>;

      case 'email':
        return <input type="email" {...attrs}></input>;

      case 'checkbox':
        return <input type="checkbox" {...attrs}></input>;

      case 'select':
        return (
          <select {...attrs}>
            <option value="" disabled selected>
              {field.placeholder}
            </option>
            {field.options.map((option) => (
              <option value={this.getOptionName(option)}>{option.text}</option>
            ))}
          </select>
        );

      case 'textarea':
        return <textarea rows={3} {...attrs}></textarea>;

      default:
        return '';
    }
  }

  private getFieldId(field: ContactFormField) {
    return `input-${field.id}`;
  }

  private getFieldName(field: ContactFormField) {
    return `field-${field.id}`;
  }

  private getOptionName(option: ContactFormFieldOption) {
    return `option-${option.id}`;
  }

  componentWillLoad(): void {
    loadFontAwesome(this.webcometUrl, this.el.shadowRoot);
  }

  componentDidLoad(): void {
    const thisArg = {
      querySelector: this.el.shadowRoot.querySelector.bind(this.el.shadowRoot),
      querySelectorAll: this.el.shadowRoot.querySelectorAll.bind(
        this.el.shadowRoot
      ),
    };

    // eslint-disable-next-line no-new-func
    const fn = new Function(this.customJs);
    try {
      fn.call(thisArg);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        `[webcomet-contact-form] failed executing custom javascript. ${err}`
      );
    }
  }

  render() {
    return (
      <Host>
        {!this.submitted ? (
          <div>
            {this.renderHeader()}
            <form onSubmit={(event) => this.handleSubmit(event)}>
              {this.fields.map((field) => (
                <div
                  class={{
                    /* eslint-disable @typescript-eslint/naming-convention */
                    form__group: true,
                    'form__group--error':
                      this.errors[`field-${field.id}`] !== undefined,
                    [`field-${field.type}`]: true,
                    [this.getFieldName(field)]: true,
                    /* eslint-enable @typescript-eslint/naming-convention */
                  }}
                >
                  <label class="form__label" htmlFor={this.getFieldId(field)}>
                    {field.label} {field.required ? '*' : ''}
                  </label>
                  {this.renderField(field)}
                  {/* {this.errors[field.name] !== undefined ? (
                    <span
                      class="form__error"
                      id={`${field.name}-error`}
                      role="alert"
                    >
                      {this.errors[field.name][0]}
                    </span>
                  ) : (
                    ''
                  )} */}
                </div>
              ))}

              {this.renderSubmitButton()}
            </form>
          </div>
        ) : (
          this.renderSuccessMessage()
        )}
      </Host>
    );
  }
}
